import * as React from 'react';
import { Alert, Button, Card, Col, Container, ListGroup, Row, Spinner } from 'react-bootstrap';
import { NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser, useCacheQuery, useCacheRecord } from 'gossamer-front-end';
import { BookingView, EventAttachment, EventExtendedView } from 'cb-model';
import { DisplayMarkdown, extractEventId } from 'shared-front-end';
import { EventPaymentReturnModal } from './EventPaymentReturnModal';
import { EventBanner } from './EventBanner';
import { EventDateTimeLocationSocialsCard } from './EventDateTimeLocationSocialsCard';
import { EventInstitutionCard, EventPersonalInstitutionCard } from './EventInstitutionCard';
import { EventBookedCard } from './EventBookedCard';
import { EventBookingCard } from './EventBookingCard';
import { EventSpecialtiesCard } from './EventSpecialtiesCard';
import { PERSONAL_INSTITUTION_ID } from 'packages/cb-unified/constants';
import { JoinLivestreamCard } from './JoinLivestreamCard';
import { EventConvosCard } from './EventConvosCard';

export const WebsiteEventDetailPage = (): React.JSX.Element => {
  const params = useParams<{ eventSlug: string }>();
  const eventId = extractEventId(params.eventSlug);
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const [paymentModalOpen, setPaymentModalOpen] = React.useState(false);
  const [searchParams] = useSearchParams();
  const redirectStatus = searchParams.get('redirect_status') as
    | 'succeeded'
    | 'failed'
    | 'canceled'
    | 'processing'
    | null;

  const eventRecord = useCacheRecord(EventExtendedView, eventId, !eventId);
  const bookingQuery = useCacheQuery(
    BookingView,
    {
      where: {
        and: [
          { fieldId: 'event_id', operator: 'eq', value: String(eventId) },
          { fieldId: 'user_id', operator: 'eq', value: user?.userId || '' },
          { fieldId: 'booking_status', operator: 'eq', value: 'active' },
        ],
      },
      order: [['ticket_booking_id', 'DESC']],
      offset: 0,
      limit: 1,
    },
    !user || !eventId,
  );
  const attachments = useCacheQuery(
    EventAttachment,
    {
      where: { and: [{ fieldId: 'event_id', operator: 'eq', value: String(eventId) }] },
      order: [['created_at', 'DESC']],
      offset: 0,
      limit: 20,
    },
    !user || !eventId,
  );

  const isBooked = !!bookingQuery.data?.length && bookingQuery.data[0].booking_status === 'active';
  const isCancelled = eventRecord.data?.visibility_limit === 'cancelled';
  const isThisEventAlreadyBooked = (bookingQuery.data?.length ?? 0) > 0;
  const isBookedAndMarkedAttendance = isThisEventAlreadyBooked && Boolean(bookingQuery.data?.[0]?.attendance_id);
  const isAdminForThisEvent =
    user?.roleId === 'staff' ||
    (user?.roleId === 'admin' && user?.orgPath === String(eventRecord.data?.institution_id));

  React.useEffect(() => {
    if (redirectStatus) {
      setPaymentModalOpen(true);
    }
  }, [redirectStatus]);

  if (!Number.isFinite(eventId)) return <Alert variant="danger">Invalid event id.</Alert>;
  if (eventRecord.error) return <Alert variant="danger">Unable to load this event.</Alert>;
  if (!eventRecord.data) return <Spinner animation="border" />;

  return (
    <Container fluid>
      {paymentModalOpen && redirectStatus && typeof eventId === 'number' && (
        <EventPaymentReturnModal
          open={paymentModalOpen}
          onOpenChange={(open) => {
            setPaymentModalOpen(open);
            if (!open) {
              // Clear Stripe return params so refresh doesn't re-open.
              navigate(location.pathname, { replace: true });
            }
          }}
          eventId={eventId}
          redirectStatus={redirectStatus}
        />
      )}
      <Row>
        <Col xs={12}>
          <EventBanner
            event={eventRecord.data}
            isEventBooked={isThisEventAlreadyBooked}
            isBookedAndMarkedAttendance={isBookedAndMarkedAttendance}
            livestreamSessionId={eventRecord.data?.livestream_session_id}
            image={eventRecord.data?.images}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <h2>{eventRecord.data?.title}</h2>
        </Col>
      </Row>

      {!!eventRecord.data?.markdown_description && (
        <Row>
          <Col xs={12}>
            <DisplayMarkdown source={eventRecord.data.markdown_description} />
          </Col>
        </Row>
      )}

      <Row>
        <Col xs={12} md={6}>
          <EventDateTimeLocationSocialsCard event={eventRecord.data} />
        </Col>

        {(!!eventRecord.data?.specialty_group || !!eventRecord.data?.specialties) && (
          <Col xs={12} md={6}>
            <EventSpecialtiesCard event={eventRecord.data} />
          </Col>
        )}

        {isAdminForThisEvent && (
          <Col xs={12} md={6}>
            <Card>
              <Card.Body>
                <p>You are an administrator for this event.</p>
                <NavLink to={`/admin/event/display/${eventRecord.data?.id}`}>
                  <Button size="sm" variant="link">
                    Event Admin Page
                  </Button>
                </NavLink>
              </Card.Body>
            </Card>
          </Col>
        )}

        {eventRecord.data?.location_type === 'online' && (
          <Col xs={12} md={6}>
            <JoinLivestreamCard
              livestreamSessionId={eventRecord.data.livestream_session_id}
              eventStartAt={eventRecord.data?.start_at}
            />
          </Col>
        )}

        <Col xs={12} md={6}>
          {!user ? (
            <Alert variant="info">Login is required for booking, attachments and event conversations.</Alert>
          ) : isCancelled ? (
            <Alert variant="warning">This event has been cancelled.</Alert>
          ) : isBooked ? (
            <EventBookedCard eventRecord={eventRecord} booking={bookingQuery.data[0]} userId={user?.userId} />
          ) : (
            <EventBookingCard eventRecord={eventRecord} />
          )}
        </Col>

        <Col xs={12} md={6}>
          {!!eventRecord.data?.institution_id && eventRecord.data?.institution_id === PERSONAL_INSTITUTION_ID && (
            <EventPersonalInstitutionCard userId={eventRecord.data?.main_host_user_id} />
          )}
          {!!eventRecord.data?.institution_id && eventRecord.data?.institution_id !== PERSONAL_INSTITUTION_ID && (
            <EventInstitutionCard institutionId={eventRecord.data?.institution_id} showAsLink showBanner showHostedBy />
          )}
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card>
            <Card.Header>Attachments</Card.Header>
            <ListGroup variant="flush">
              {attachments.data?.map((item) => (
                <ListGroup.Item key={item.id}>
                  <a href={item.attachment} target="_blank" rel="noreferrer">
                    {item.title}
                  </a>
                </ListGroup.Item>
              ))}
              {!attachments.data?.length && <ListGroup.Item>No attachments available.</ListGroup.Item>}
            </ListGroup>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <EventConvosCard
            isBooked={isBooked}
            eventId={eventId}
            eventTitle={eventRecord.data?.title || ''}
            eventInstitutionId={eventRecord.data?.institution_id || 0}
            mainHostUserId={eventRecord.data?.main_host_user_id || ''}
          />
        </Col>
      </Row>
    </Container>
  );
};
